import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';
import { useMapStore } from '../../store/useMapStore';
import Delaunator from 'delaunator';
import GUI from "lil-gui";
import { useLayerStore } from '../../store/useLayerStore';
import { useEntityStore } from '../../store/useExportStore';
import { geometryToJSON } from '../../utils/GeometryHelper';
import { Stage, GeometryType } from '../../constants/progress';
import { useProgressStore } from '../../store/progressStore';
import { useGeometrySelectionStore } from '../../store/useGeometrySelectionStore';

const TopographyGeometry = ({ 
  entity, 
  scalingFactor, 
  selectedEntityId, 
  setSelectedEntityId,
  onEntitySelect,
  className,
  visibilty
}) => {


  const meshRef = useRef();
  const geometryType = GeometryType.TOPOGRAPHY;
  const updateProgress = useProgressStore.getState().updateProgress;
  const [mesh, setMesh] = useState(false); 
  const [topoopacity, settopoopacity] = useState(1.0);
  const [topoColor, settopoColor] = useState("#c9d9cb");
 
  const clearTopography = useEntityStore((state) => state.clearTopography);
  const setTopographyEntity = useEntityStore((state) => state.setTopographyEntity);
  const { selectTopography, isTopographySelected, selectedType } = useGeometrySelectionStore();
  
  // Create geometry from points using Delaunay triangulation
  const geometry = useMemo(() => {
    if (!entity || !entity.mercatorCoordinates || entity.mercatorCoordinates.length === 0) {
     
      return null;
    }
   
    const points = entity.mercatorCoordinates;   
    const scale = 1;
    const vertices2D = points.map(p => [p.x * scale, p.y * scale]);
    const delaunay = Delaunator.from(vertices2D);
    const indices = Array.from(delaunay.triangles);
    const geo = new THREE.BufferGeometry();
    const positions = new Float32Array(    
      points.flatMap(p => [
        p.x * scale,
        p.z * scale,
       -p.y * scale
      ])
    );
    geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geo.setIndex(indices);
    geo.computeVertexNormals();
    geo.userData = {
      name: entity.name, 
      breitengrad: entity.breitengrad,
      laengengrad: entity.laengengrad, 
      nhn: entity.nhn, 
      referenceSystem: entity.referenceSystem,
      id: entity.id || entity.name // Use id or name for comparison
    };
    // Initialize vertex colors with base color
    const positionCount = geo.attributes.position.count;
    const colors = new Float32Array(positionCount * 3);
    const baseColor = new THREE.Color("#c9d9cb");
    
    for (let i = 0; i < positionCount; i++) {
      colors[i * 3] = baseColor.r;
      colors[i * 3 + 1] = baseColor.g;
      colors[i * 3 + 2] = baseColor.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  
    return geo;
  }, [entity, scalingFactor]);
  
  // Reset colors when selection is cleared (background or other geometry clicked)
  useEffect(() => {
    if (!geometry || !geometry.attributes.color) return;
    
    // Get entity ID
    const entityId = entity?.id || entity?.name;
    
    // If no topography is selected (background clicked or other geometry selected)
    if (selectedType !== 'topography') {
      const positionCount = geometry.attributes.position.count;
      const colors = geometry.attributes.color.array;
      const baseColor = new THREE.Color(topoColor);
      
      // Reset all vertices to base color
      for (let i = 0; i < positionCount; i++) {
        colors[i * 3] = baseColor.r;
        colors[i * 3 + 1] = baseColor.g;
        colors[i * 3 + 2] = baseColor.b;
      }
      
      geometry.attributes.color.needsUpdate = true;
      setSelectedEntityId(null);
    }
    // If a different topography is selected, check if our current selection needs to be cleared
    else if (entityId && selectedEntityId && !isTopographySelected(entityId)) {
      const positionCount = geometry.attributes.position.count;
      const colors = geometry.attributes.color.array;
      const baseColor = new THREE.Color(topoColor);
      
      // Reset all vertices to base color
      for (let i = 0; i < positionCount; i++) {
        colors[i * 3] = baseColor.r;
        colors[i * 3 + 1] = baseColor.g;
        colors[i * 3 + 2] = baseColor.b;
      }
      
      geometry.attributes.color.needsUpdate = true;
      setSelectedEntityId(null);
    }
  }, [selectedType, selectedEntityId, isTopographySelected, geometry, topoColor, entity, setSelectedEntityId]);

  const handleClick = useCallback((event) => {
    event.stopPropagation();

    if (!visibilty) {
      console.log('Topography layer not visible, ignoring click');
      return;
    }

    if (!meshRef.current) {
      console.log('No mesh ref');
      return;
    }
    
    const intersects = event.intersections;
    
    if (intersects && intersects.length > 0) {
      const intersection = intersects[0];
      const clickedObject = intersection.object;
      
      if (clickedObject === meshRef.current) {
        console.log('Topography entity selected:', entity);
        
        // Get entity ID
        const entityId = entity?.id || entity?.name;
        
        // Update store
        selectTopography(entityId);
        setSelectedEntityId(entityId);
        
        // Update colors immediately
        if (geometry && geometry.attributes.color) {
          const positionCount = geometry.attributes.position.count;
          const colors = geometry.attributes.color.array;
          const selectedColor = new THREE.Color('#2B323A');
          
          for (let i = 0; i < positionCount; i++) {
            colors[i * 3] = selectedColor.r;
            colors[i * 3 + 1] = selectedColor.g;
            colors[i * 3 + 2] = selectedColor.b;
          }
          
          geometry.attributes.color.needsUpdate = true;
        }
        
        if (entity && onEntitySelect) {
          onEntitySelect(entity);
        }
      }
    } else {
      console.log('No intersections found');
    }
  }, [entity, onEntitySelect, visibilty, className, geometry, selectTopography, setSelectedEntityId]);

  // useEffect(() => {
  //   const params = { meshOn: false, showControls: visibilty, topoColor: "#f1e9e9", topoopacity: 1.0 };
  //   const gui = new GUI();
    
  //   const topoFolder = gui.addFolder("Topo Surface");
         
  //   topoFolder
  //     .addColor(params, "topoColor")
  //     .name("Color")
  //     .onChange((value) => {
  //       settopoColor(value);
  //       const color = new THREE.Color(value);
  //       const r = Math.round(color.r * 255);
  //       const g = Math.round(color.g * 255);
  //       const b = Math.round(color.b * 255);
  //       console.log(`Topo RGB: (${r}, ${g}, ${b})`);
  //     });
    
  //   topoFolder.open();
  //   const guidom = gui.domElement;
  //   guidom.style.right = "100px";
  //   guidom.style.top = "140px";

  //   if (params.showControls) {
  //     gui.domElement.style.display = "block"; 
  //   } else {
  //     gui.domElement.style.display = "none";
  //   }

  //   return () => gui.destroy(); 
  // }, [visibilty]);

  useEffect(() => {
    if (visibilty) {
      const topography = {
        topographyProperties: geometry.userData,
        topography: geometryToJSON(geometry)
      };
      setTopographyEntity({ type: "topography", topography });
      //console.log("Topography geometry as json: ", topography)
    }
    if (!visibilty) {
      clearTopography();
    }
  }, [visibilty, geometry, setTopographyEntity, clearTopography]);

  useEffect(()=>{
    if(geometry)
    {
      updateProgress(geometryType,{
        stage: Stage.DONE,
        message:'Geometry creation completed' ,
        progress:100

      })
    }
    else{
      updateProgress(geometryType,{
        stage: Stage.ERROR,
        message: 'Failed to create topography',
        progress:75
      })
    }

  },[geometry])

  useEffect(()=>{
    if (!entity?.mercatorCoordinates?.length) {
    updateProgress(geometryType, {
      stage: Stage.NULL,
      message: 'No topography entity / coordinates found',
      error: 'Server provided no coordinates'
    });
    return;
    }
  
  },[entity])

  if (!geometry) {
    return null;
  }

  return (
    <group visible={visibilty}>
      {visibilty && (
        <mesh 
          ref={meshRef} 
          geometry={geometry}
          onClick={handleClick}
        >
          <meshStandardMaterial 
            vertexColors={true}
            opacity={topoopacity}
            wireframe={mesh}
            side={THREE.DoubleSide}
            transparent={topoopacity < 1}
          />
        </mesh>
      )}
    </group>
  );
};

export default TopographyGeometry;